import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useNotificationStore } from '@/stores/notification';
import { useLoadingStore } from '@/stores/loading';
import { useTenantStore } from '@/stores/tenant';
import { isTenantHeaderRequired } from '@/utils/tenantUtils';
import { getTenantErrorCodeFromResponse } from '@/services/tenantService';
import router from '@/router';

// Extend Axios config to allow metadata for performance tracking
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
    showSuccessNotification?: boolean;
    showErrorNotification?: boolean;
    showLoadingIndicator?: boolean;
    operation?: string;
  };
}

// Get API base URL from environment
const getApiBaseUrl = (): string => {
  // Use Vite environment variable
  if (import.meta.env.VITE_API_BASE_URL) {
    return normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL);
  }

  // For development, use relative path to leverage Vite proxy
  // This avoids CORS issues by routing through the proxy
  return '/api/v1';
};

// Validate environment variable
const apiBaseUrl = getApiBaseUrl();
if (!apiBaseUrl) {
  throw new Error(
    'API base URL is not defined. Please set VITE_API_BASE_URL in your environment variables.'
  );
}

console.log('API Base URL:', apiBaseUrl);
console.log('Environment:', import.meta.env.MODE);
console.log('VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);

// Create Axios instance
const http: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
http.interceptors.request.use(
  (config: CustomAxiosRequestConfig) => {
    if (config.headers && !config.headers['Authorization']) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // X-Tenant-ID: attach for all requests except excluded paths (health, docs, admin)
    if (config.headers && isTenantHeaderRequired(config.url)) {
      try {
        const tenantStore = useTenantStore();
        const tenantId = tenantStore.currentTenantId;
        if (tenantId) {
          config.headers['X-Tenant-ID'] = tenantId;
        }
      } catch {
        // Pinia not ready (e.g. during SSR or before mount)
      }
    }

    // Add performance tracking and notification metadata
    config.metadata = {
      startTime: Date.now(),
      showSuccessNotification: config.metadata?.showSuccessNotification ?? true,
      showErrorNotification: config.metadata?.showErrorNotification ?? true,
      showLoadingIndicator: config.metadata?.showLoadingIndicator ?? false,
      operation: config.metadata?.operation,
    };

    // Show loading indicator for non-GET requests
    if (config.method !== 'get' && config.metadata.showLoadingIndicator) {
      const loadingStore = useLoadingStore();
      loadingStore.show();
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor
http.interceptors.response.use(
  (response: AxiosResponse) => {
    // Track API performance
    const config = response.config as CustomAxiosRequestConfig;
    const duration = Date.now() - (config.metadata?.startTime || Date.now());
    console.log(`API Call: ${response.config.url} took ${duration}ms`);

    // Stop loading indicator if it was shown
    if (config.metadata?.showLoadingIndicator) {
      const loadingStore = useLoadingStore();
      loadingStore.hide();
    }

    // Show success notification for non-GET requests
    if (config.method !== 'get' && config.metadata?.showSuccessNotification) {
      const notificationStore = useNotificationStore();
      const operation = config.metadata.operation || getOperationFromMethod(config.method || '');
      // Use the raw translation key - the notification store should handle translation
      notificationStore.showSuccess(getSuccessMessage(operation));
    }

    return response;
  },
  (error: AxiosError) => {
    const config = error.config as CustomAxiosRequestConfig;

    // Stop loading indicator if it was shown
    if (config?.metadata?.showLoadingIndicator) {
      const loadingStore = useLoadingStore();
      loadingStore.hide();
    }

    // Tenant-related errors (400/403): redirect to tenant error screen
    if (error.response && (error.response.status === 400 || error.response.status === 403)) {
      const body = error.response.data as { error?: string } | undefined;
      const code = getTenantErrorCodeFromResponse(error.response.status, body);
      if (code) {
        try {
          const tenantStore = useTenantStore();
          tenantStore.setInvalid(code);
          void router.push('/tenant-error');
        } catch {
          window.location.href = '/tenant-error';
        }
        return Promise.reject(error);
      }
    }

    // Handle authentication errors
    // Special-case login failures so we can show the backend's error
    // message instead of force-redirecting back to /login.
    const isLoginRequest =
      config?.url?.includes('/auth/signin') || config?.url?.includes('/auth/login');
    // Don't redirect when on public booking page so errors show in-page
    const isPublicBookingPage =
      typeof window !== 'undefined' && window.location.pathname.startsWith('/book/');
    if (
      error.response &&
      error.response.status === 401 &&
      !isLoginRequest &&
      !isPublicBookingPage
    ) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Show error notification if enabled
    if (config?.metadata?.showErrorNotification) {
      const notificationStore = useNotificationStore();
      const errorMessage = getErrorMessage(error);
      notificationStore.showError(errorMessage);
    }

    // Handle network errors
    if (!error.response && error.request) {
      console.error('Network error:', error.message);
      return Promise.reject(error);
    }

    // Log structured error responses for debugging
    if (error.response?.data && import.meta.env.DEV) {
      console.error('API Error Response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.config?.url,
        data: error.response.data,
      });
    }

    return Promise.reject(error);
  }
);

// Helper: ensure base URL always ends with /api/v1 (frontend always uses v1)
function normalizeApiBaseUrl(rawBaseUrl: string): string {
  const trimmed = rawBaseUrl.replace(/\/+$/, '');
  if (trimmed.endsWith('/api/v1')) {
    return trimmed;
  }
  if (trimmed.endsWith('/api')) {
    return `${trimmed}/v1`;
  }
  // Bare host or other path → append /api/v1
  return `${trimmed}/api/v1`;
}
function getOperationFromMethod(method: string): string {
  switch (method.toLowerCase()) {
    case 'post':
      return 'created';
    case 'put':
    case 'patch':
      return 'updated';
    case 'delete':
      return 'deleted';
    default:
      return 'completed';
  }
}

function getSuccessMessage(operation: string): string {
  // If the operation already contains a dot, it's likely a full translation key
  // Otherwise, prefix it with api.success
  if (operation.includes('.')) {
    return operation;
  }
  return `api.success.${operation}`;
}

function getErrorMessage(error: AxiosError): string {
  if (error.response?.data) {
    const data = error.response.data as any;
    if (data.message) return data.message;
    if (data.error) return data.error;
  }

  if (error.message) return error.message;

  return 'api.errors.generic';
}

// Wrapper functions to handle metadata
const httpWithMetadata = {
  get: <T = any>(url: string, config?: any) => {
    const { metadata, ...axiosConfig } = config || {};
    if (metadata) {
      (axiosConfig as any).metadata = metadata;
    }
    return http.get<T>(url, axiosConfig);
  },
  post: <T = any>(url: string, data?: any, config?: any) => {
    const { metadata, ...axiosConfig } = config || {};
    if (metadata) {
      (axiosConfig as any).metadata = metadata;
    }
    return http.post<T>(url, data, axiosConfig);
  },
  put: <T = any>(url: string, data?: any, config?: any) => {
    const { metadata, ...axiosConfig } = config || {};
    if (metadata) {
      (axiosConfig as any).metadata = metadata;
    }
    return http.put<T>(url, data, axiosConfig);
  },
  patch: <T = any>(url: string, data?: any, config?: any) => {
    const { metadata, ...axiosConfig } = config || {};
    if (metadata) {
      (axiosConfig as any).metadata = metadata;
    }
    return http.patch<T>(url, data, axiosConfig);
  },
  delete: <T = any>(url: string, config?: any) => {
    const { metadata, ...axiosConfig } = config || {};
    if (metadata) {
      (axiosConfig as any).metadata = metadata;
    }
    return http.delete<T>(url, axiosConfig);
  },
};

// Export the configured HTTP client
export default httpWithMetadata;

// Export individual methods for convenience
export const { get, post, put, patch, delete: del } = httpWithMetadata;
